/**
 * Whether the plan is drawn on the washed map behind the five form steps
 * (`spec-onboarding-persistent-scene-wash`, R2.4; `onboarding-persistent-scene-twin-details-map`, R2).
 * In a module of its own, importing nothing heavier than the twin form's rules, so the page reads it
 * (the handover's cut, R6.1) without pulling the renderer's chunk in, and the washed layer reads the
 * very same rule.
 *
 * It no longer decides whether the form steps have a map: they always do (twin-details-map R1), at
 * the anchor's fallback. This answers only whether the plan is drawn on it, which needs a real stored
 * position: `planSceneAnchor` finds an origin for any record, and a plan drawn at a fallback would
 * show the twin somewhere it is not.
 */
import type { AdminTwin, FloorPlannerLayout } from '@repo/frontend-types';
import { looksLikePlaceholderOrigin } from '../../../pages/Onboarding/twinDetailsForm';

/** The page's read of the session plan (`usePlanLoad`'s `planState`). */
export type WashedPlanRead = 'loading' | 'none' | 'loaded' | 'failed' | 'unread';

/** A stored coordinate as text: the record's type says string, the wire may send null or a number. */
const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : typeof value === 'number' ? String(value) : '';

/**
 * True only when the twin record has a finite stored latitude and longitude that is not the legacy
 * placeholder. One definition, read by the plan's rule below and by the page's plan-without-position
 * message (twin-details-map R2.3).
 */
export const hasRealPosition = (twinRecord: AdminTwin | null | undefined): boolean => {
  if (!twinRecord) return false;
  const latitude = text(twinRecord.latitude);
  const longitude = text(twinRecord.longitude);
  if (latitude === '' || longitude === '') return false;
  if (!Number.isFinite(Number(latitude)) || !Number.isFinite(Number(longitude))) return false;
  return !looksLikePlaceholderOrigin({ latitude, longitude });
};

/**
 * True only when the twin has a real position (`hasRealPosition`), and the plan read has loaded a
 * plan with at least one floor. Anything missing, blank, unreadable, still loading or failed is
 * false, as is a plan whose floors are not a list: nothing is coerced.
 */
export const washedPlanAvailable = ({
  twinRecord,
  planRead,
  layout,
}: {
  twinRecord: AdminTwin | null | undefined;
  planRead: WashedPlanRead | undefined;
  layout: FloorPlannerLayout | null | undefined;
}): boolean => {
  // A resumed layout comes from a JSON column, so `floors` can be any shape (PR review).
  const floors: unknown = layout?.floors;
  if (!twinRecord || planRead !== 'loaded' || !Array.isArray(floors) || floors.length === 0) {
    return false;
  }
  return hasRealPosition(twinRecord);
};
